"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAppSyncLink = void 0;
const context_1 = require("@apollo/client/link/context");
const sha256_universal_1 = require("@aws-crypto/sha256-universal");
const signature_v4_1 = require("@aws-sdk/signature-v4");
const printer_1 = require("graphql/language/printer");
const AWS4_SERVICE = 'appsync';
/**
 * Cleans up any temporary variables to avoid polluting the AWS Signature V4 signinig process
 * @param variables - The variables object from the ApolloLink request
 * @returns the cleaned variables object or undefined if no variables exist
 */
const removeTempVars = (variables) => {
    if (!variables)
        return undefined;
    else
        return Object.keys(variables)
            .filter((k) => !k.startsWith("@@"))
            .reduce((acc, key) => {
            acc[key] = variables[key];
            return acc;
        }, {});
};
/**
 * Generates a concise request object that can be used by AWS Signature V4 signing
 * @param request - The Operation from an ApolloLink request
 * @param headers - Key-value pairs of additional headers to be included on the request
 * @returns a request object for use with AWS Signature V4 signing
 */
const formatAsRequest = (request, headers) => {
    const body = {
        operationName: request.operationName,
        variables: removeTempVars(request === null || request === void 0 ? void 0 : request.variables),
        query: (0, printer_1.print)(request.query)
    };
    return {
        body: JSON.stringify(body),
        method: 'POST',
        headers: Object.assign({ accept: '*/*', 'content-type': 'application/json; charset=UTF-8' }, headers),
    };
};
/**
 * Gets the request headers from a signed AWS Signature V4
 * @param request - The Apollo graphql request passed by ApolloLink
 * @param appSyncConfig - an object containing the AWS region and endpoint url (region, url)
 * @param clockDrift - The amount of clock drift in seconds, this can be found on the Amplify session.
 * @returns signed AWS V4 Headers that can be set directly on the Apollo context
 */
const getAws4SignedHeaders = (AmplifyAuth, request, appSyncConfig, clockDrift = 0) => __awaiter(void 0, void 0, void 0, function* () {
    const region = appSyncConfig.region;
    const url = new URL(appSyncConfig.url);
    const { accessKeyId, sessionToken, secretAccessKey } = yield AmplifyAuth.currentCredentials();
    const signatureV4 = new signature_v4_1.SignatureV4({
        credentials: { accessKeyId, sessionToken, secretAccessKey },
        region,
        service: AWS4_SERVICE,
        sha256: sha256_universal_1.Sha256
    });
    const reqHeaders = {
        Host: url.host
    };
    let signingDate = new Date();
    // if the client time is different to the server time then adjust
    //  the signing date to prevent clock drift errors from AWS
    //  note: re-login is required if system clock is adjusted
    if (clockDrift && clockDrift != 0) {
        signingDate = new Date(new Date().getTime() - (clockDrift * 1000));
    }
    const formatted = Object.assign(Object.assign({}, formatAsRequest(request, reqHeaders)), { protocol: url.protocol, hostname: url.hostname, path: url.pathname });
    const { headers } = yield signatureV4.sign(formatted, { signingDate });
    return headers;
});
/**
 * Create an ApolloLink that signes the request with AWS Signature V4 headers and a cognito token
 * @returns an ApolloLink that can be added directly to the Link pipeline
 */
const createAppSyncLink = (AmplifyAuth, appSyncConfig) => (0, context_1.setContext)((request, previousContext) => __awaiter(void 0, void 0, void 0, function* () {
    // The type cast is needed below because for some reason clockDrift doesn't exist on the types
    //  even though it exists in the documentation and at runtime
    let sessionCredentials;
    try {
        sessionCredentials = (yield AmplifyAuth.currentSession());
    }
    catch (error) {
        console.log('Session Credentials Error:', error);
    }
    const headers = yield getAws4SignedHeaders(AmplifyAuth, request, appSyncConfig, sessionCredentials === null || sessionCredentials === void 0 ? void 0 : sessionCredentials.clockDrift);
    if (sessionCredentials) {
        const cognitoToken = sessionCredentials.getIdToken().getJwtToken();
        return {
            headers: Object.assign(Object.assign(Object.assign({}, previousContext.headers), headers), { cognitoToken }),
        };
    }
    return {
        headers: Object.assign(Object.assign({}, previousContext.headers), headers),
    };
}));
exports.createAppSyncLink = createAppSyncLink;
